import { calendar_event_list_item } from "@/app/typing/events";
import { document_list_item } from "@/app/typing/documents";

/**
 * A note is written about at most one source: a clinical event or a research
 * document. Both columns live on the artifact, so "no source" is both null.
 */
export type ArtifactSource = { event_id: number | null; document_id: number | null };

/**
 * The picker is one numeric column, so the two id spaces are folded into one:
 * events keep their id, documents are negated. Ids are always positive, so the
 * sign is free to carry the type.
 */
export const encodeSource = (source: { event_id?: number | null; document_id?: number | null } | null | undefined): number | undefined => {
    if (source?.event_id) return source.event_id;
    if (source?.document_id) return -source.document_id;
    return undefined;
};

export const decodeSource = (value: number | null | undefined): ArtifactSource => {
    if (!value) return { event_id: null, document_id: null };
    return value > 0
        ? { event_id: value, document_id: null }
        : { event_id: null, document_id: -value };
};

/**
 * The objects-panel parent for a note: it nests under its source. Event wins
 * when both are set, matching the backend's _source_for_selected.
 */
export const sourceParent = (
    source: { event_id?: number | null; document_id?: number | null } | null | undefined,
): { type: "event" | "document"; id: number } | undefined => {
    if (source?.event_id) return { type: "event", id: source.event_id };
    if (source?.document_id) return { type: "document", id: source.document_id };
    return undefined;
};

/**
 * Whether an object type can own notes. Sources are the grouping level in the
 * objects panel and the tab bar: they render as a group with their notes inside.
 */
export const isSourceType = (type: string | undefined): boolean => type === "event" || type === "document";

export type SourceOption = {
    id: number;
    name: string;
    type: "event" | "document";
    tag_id: number;
};

/** Events and documents as one list of picker options, in encoded id space. */
export const sourceOptions = (
    events: calendar_event_list_item[] | undefined,
    documents: document_list_item[] | undefined,
    tagIds: number[] | undefined,
): SourceOption[] => {
    const options: SourceOption[] = [
        ...(events ?? []).map((e) => ({ id: e.id, name: e.title, type: "event" as const, tag_id: e.tag_id })),
        ...(documents ?? []).map((d) => ({ id: -d.id, name: d.variable_name, type: "document" as const, tag_id: d.tag_id })),
    ];
    // The chat is scoped to tags, so only sources the note could belong to.
    return tagIds && tagIds.length > 0 ? options.filter((o) => tagIds.includes(o.tag_id)) : options;
};
